<script lang="ts" setup>
import type { Section } from '~/types'

interface Props extends Pick<Section, 'collection' | 'id' | 'description' | 'subtitle' | 'subdescription' | 'theme'> { title: string }

const props = defineProps<Props>()
const pb = useNuxtApp().$pb
const fullCollection = GetCollection(props.collection)
const { data } = useAsyncData(`highlights-${props.id}-${props.collection}`, async () => {
  const c = GetCollection(props.collection)
  const collection = c?.pub?.collection ?? c?.collection
  if (!collection)
    return ['add']
  const data = await pb.collection(collection).getList(1, 5, { sort: '@random' }).catch(() => EMPTY_LIST_RESULT)
  return data.items
})

const scroller = ref<HTMLDivElement | null>(null)
const position = ref(0) // Tracks the current scroll position
const speed = ref(1) // Pixels per frame
let animationFrame: number | null = null

// Start scrolling
const startScrolling = () => {
  const step = () => {
    position.value -= speed.value // Update position
    const scrollerEl = scroller.value
    if (!scrollerEl) return

    // Reset position if scrolled past duplicate
    const scrollWidth = scrollerEl.scrollWidth / 2
    if (Math.abs(position.value) >= scrollWidth) {
      position.value = 0
    }

    // Apply the transform
    scrollerEl.style.transform = `translateX(${position.value}px)`
    animationFrame = requestAnimationFrame(step)
  }

  animationFrame = requestAnimationFrame(step)
}

// Adjust speed on hover
const slowScroll = () => {
  speed.value = 0.2 // Slow down
}

const normalScroll = () => {
  speed.value = 1 // Restore normal speed
}

// Lifecycle hooks
onMounted(() => {
  startScrolling() // Start scrolling on mount
})

onBeforeUnmount(() => {
  if (animationFrame) cancelAnimationFrame(animationFrame) // Clean up on unmount
})
</script>

<template>
  <div class="mb-32 mt-12 w-full">
    <div
      class="mx-auto max-w-4xl  px-6"
      :class="[theme]"
    >
      <HeadingText
        v-motion
        :visible="{ opacity: 1, y: 0, scale: 1 }"
        :initial="{ opacity: 0, scale: 0.5 }"
        :delay="350"
        tag="span"
        :theme="[theme ?? 'blue']"
        gradient
        class="mb-2 heading inline-flex justify-center font-bold uppercase text-blue font-display w-full text-6xl md:text-7xl lg:text-8xl 2xl:text-9xl "
      >
        {{ props.title }}
      </HeadingText>
      <p class="text-2xl md:text-3xl lg:text-4xl font-display font-black uppercase py-3 text-blue">
        {{ props.subtitle }}
      </p>
      <section
        class="htmldescription"
        data-allow-mismatch
        v-html="props.description"
      />
    </div>
    <div
      class="relative overflow-hidden w-full pt-20"
      @mouseover="slowScroll"
      @mouseleave="normalScroll"
    >
      <div
        ref="scroller"
        class="flex items-center scroller gap-32 lg:gap-48 xl:gap-64 2xl:gap-96"
      >
        <!-- Main Items -->
        <div
          v-for="(item, index) in data"
          :key="index"
          class="flex-shrink-0"
        >
          <IconWithLink
            :collection="fullCollection"
            :item="item"
            :index="index"
          />
        </div>
        <!-- Duplicate Items for Seamless Looping -->
        <div
          v-for="(item, index) in data"
          :key="'duplicate-' + index"
          class="flex-shrink-0"
        >
          <IconWithLink
            :collection="fullCollection"
            :item="item"
            :index="index"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scroller {
  display: flex;
  will-change: transform; /* Optimize performance */
}
</style>
